import { useState } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { useIsMobile } from "@/hooks/use-mobile"
import {
  LayoutDashboard,
  Settings,
  X,
  PanelLeft,
  Loader2,
  Users,
  LogOut,
  Truck,
  Sparkles,
  Brain,
  BarChart3,
  Wrench,
  Palette,
  ShoppingBag,
  Plug,
  Eye,
  Tags,
  CalendarDays,
  Boxes,
  PackageSearch,
  ShoppingCart,
} from "lucide-react"
import logoDobraImg from "@/assets/logo-dobra.gif"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useDensityMode } from "@/hooks/useDensityMode"
import { getDensityContextKey } from "@/utils/densityContext"
import { NotificationBell } from "@/components/NotificationBell"
import { RealtimeStatusIndicator } from "@/components/RealtimeStatusIndicator"
import { ThemeToggle } from "@/components/ThemeToggle"
import { isAdmin as checkIsAdmin, getRoleLabel, getRoleIcon, isManager as checkIsManager } from "@/utils/roleHelpers"
import { useAuth } from "@/contexts/AuthContext"
import { useSectors } from "@/hooks/useSectors"
import { SectorSwitcher } from "@/components/SectorSwitcher"
import { AdminPanelLink } from "@/components/AdminPanelLink"

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar"

interface AppSidebarProps {
  currentView?: string
  onViewChange?: (view: string) => void
  userRole?: any
  onSignOut?: () => void
  densityMode?: string
  onCycleDensity?: () => void
  getDensityLabel?: () => string
  getDensityIcon?: () => string
}

// Menu items internos do ManagerDashboard.
// Itens com `id` usam onViewChange (view interna do dashboard).
// Itens com `path` usam Link (rota dedicada) — permite mesclar rotas
// dedicadas no grupo visual sem quebrar a posicao historica.
type DashboardMenuItem = {
  label: string;
  icon: any;
  adminOnly?: boolean;
} & ({ id: string; path?: undefined } | { path: string; id?: undefined });

const dashboardMenuItems: DashboardMenuItem[] = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  // #245 (reunião de entrega 2026-06-10): "Quadro de Produção", "Linha do Tempo",
  // "Histórico" e "Análises" removidos do sidebar (itens não utilizados). Em
  // 2026-10-08 (auditoria D20/DEC-12) as views ?view=history/board/timeline/
  // analytics saíram do ManagerDashboard também; HistoryPage e ProductionBoard
  // estão em _archive/2026-10-08/.
  { path: "/usuarios", label: "Usuários", icon: Users, adminOnly: true },
  { id: "settings", label: "Configurações", icon: Settings },
]

// Rotas dedicadas (usam Link)
const dedicatedRoutes: Array<{
  path: string;
  label: string;
  icon: any;
  managerOnly?: boolean;
  adminOnly?: boolean;
  superAdminOnly?: boolean;
  /** Com superAdminOnly, também mostra para o admin da organização (org_admin) — ex.: Relatórios/auditoria. */
  orgAdminAllowed?: boolean;
  sectors?: string[];
  operatorOnly?: boolean;
}> = [
  { path: "/painel", label: "📊 Painel de Produção", icon: LayoutDashboard, adminOnly: true },
  { path: "/scheduling", label: "🤖 Programação", icon: Sparkles, managerOnly: true },
  { path: "/intelligence", label: "🧠 Inteligência", icon: Brain, managerOnly: true },
  { path: "/retrabalho", label: "🔧 Retrabalho", icon: Wrench, managerOnly: true },
  { path: "/relatorios", label: "📈 Relatórios", icon: BarChart3, superAdminOnly: true, orgAdminAllowed: true },
  { path: "/feedbacks", label: "⭐ Feedbacks", icon: Sparkles, managerOnly: true },
  { path: "/operador", label: "🔧 Operador", icon: Wrench, operatorOnly: true, sectors: ['CORTE', 'COSTURA', 'SILK', 'DTF', 'SUBLIMACAO', 'BORDADO', 'IMPRESSAO', 'SAIDA_ESTOQUE', 'COMPRAS', 'EXPEDICAO'] },
  { path: "/logistics", label: "🚚 Logística", icon: Truck, sectors: ['LOGISTICA'] },
  { path: "/arte", label: "🎨 Arte Finalista", icon: Palette, sectors: ['ARTE'] },
  { path: "/comercial", label: "💼 Comercial", icon: ShoppingBag, sectors: ['COMERCIAL'] },
  { path: "/estoque", label: "📦 Estoque", icon: Boxes, managerOnly: true },
  // sectors: ['ESTOQUE'] apenas — o cortador que ACUMULA o setor entra por aqui,
  // e admin/gerente entram pelo curto-circuito de papel. Incluir 'CORTE' daria a
  // tela a todo cortador, inclusive quem não opera o estoque.
  { path: "/estoquista", label: "📥 Posto de Estoque", icon: PackageSearch, sectors: ['ESTOQUE'] },
  // sectors: ['COMPRAS'] — o comprador entra por aqui, e gerente/admin pelo
  // curto-circuito de papel. A rota de LOGIN do setor COMPRAS continua sendo
  // /operador: trocá-la realocaria o comprador de tela no dia do merge,
  // tirando dele o quadro com as compras avulsas que ele usa hoje.
  { path: "/compras-demandas", label: "🛒 Compras", icon: ShoppingCart, sectors: ['COMPRAS'] },
  { path: "/cadastros", label: "🏷️ Cadastros", icon: Tags, adminOnly: true },
  { path: "/feriados", label: "📅 Feriados", icon: CalendarDays, managerOnly: true },
  { path: "/integracoes", label: "🔌 Integrações", icon: Plug, managerOnly: true },
  { path: "/admin/automacao", label: "🤖 Regras de Automação", icon: Settings, adminOnly: true },
  { path: "/como-funciona", label: "ℹ️ Como Funciona", icon: Eye, managerOnly: true },
]

export function AppSidebar({
  currentView,
  onViewChange,
  userRole: propUserRole,
  onSignOut: propOnSignOut,
  densityMode: propDensityMode,
  onCycleDensity: propOnCycleDensity,
  getDensityLabel: propGetDensityLabel,
  getDensityIcon: propGetDensityIcon
}: AppSidebarProps) {
  const { state, toggleSidebar, openMobile } = useSidebar()
  const location = useLocation()
  const navigate = useNavigate()
  const isCollapsed = state === "collapsed"
  const [isHidden, setIsHidden] = useState(true)
  const isMobile = useIsMobile()
  
  // Hooks internos - usar quando props não são passadas
  const { userRole: authUserRole, signOut: authSignOut } = useAuth()
  const { data: sectors = [], isLoading: loadingSectors } = useSectors()
  
  // Usar props se passadas, senão usar hooks internos
  const userRole = propUserRole ?? authUserRole;
  
  // Determinar contexto de densidade baseado na rota atual e setor do usuário
  const densityContextKey = getDensityContextKey({ 
    pathname: location.pathname, 
    userRole 
  });
  
  const { 
    densityMode: hookDensityMode, 
    cycleDensityMode: hookCycleDensity, 
    getDensityLabel: hookGetDensityLabel, 
    getDensityIcon: hookGetDensityIcon 
  } = useDensityMode(densityContextKey)
  
  
  // Usar props se passadas, senão usar hooks
  const onSignOut = propOnSignOut ?? authSignOut;
  const effectiveDensityMode = propDensityMode ?? hookDensityMode
  const effectiveCycleDensity = propOnCycleDensity ?? hookCycleDensity
  const effectiveGetDensityLabel = propGetDensityLabel ?? hookGetDensityLabel
  const effectiveGetDensityIcon = propGetDensityIcon ?? hookGetDensityIcon
  
  // Determinar permissões baseado no userRole
  const isAdminUser = checkIsAdmin(userRole)
  const isManager = checkIsManager(userRole)
  const isSuperAdminUser = userRole?.isSuperAdmin === true
  const isOrgAdminUser = userRole?.isOrgAdmin === true
  const isArteUser = userRole?.setor === 'ARTE'
  const isLogisticsUser = userRole?.setor === 'LOGISTICA'
  // Apenas setores de produção (excluindo ARTE, COMERCIAL, LOGISTICA que têm rotas dedicadas)
  const productionSectors = sectors.filter(s => s.tipo === 'PRODUCAO')
  
  // Detectar página/view atual baseado na rota
  const getActiveView = (): string => {
    if (currentView) return currentView
    
    const path = location.pathname
    const params = new URLSearchParams(location.search)
    const viewParam = params.get('view')
    
    if (path === '/scheduling') return 'scheduling'
    if (path === '/intelligence') return 'intelligence'
    if (path === '/relatorios') return 'relatorios'
    if (path === '/logistics' || path === '/logistics/history') return 'logistics'
    if (path === '/operador') return 'operator'
    if (viewParam) return viewParam
    return 'dashboard'
  }
  
  const activeView = getActiveView()
  const isOnDashboard = location.pathname === '/' || location.pathname === ''
  
  // Navegação inteligente
  const handleNavigation = (viewId: string) => {
    if (onViewChange && isOnDashboard) {
      // Se estamos no ManagerDashboard e temos onViewChange, usar navegação interna
      onViewChange(viewId)
    } else {
      // Senão, navegar para o dashboard com o view
      navigate(`/?view=${viewId}`)
    }
  }
  
  // Se hidden, mostrar apenas botão flutuante para reabrir (apenas desktop)
  const shouldRenderSidebar = isMobile || !isHidden

  if (!shouldRenderSidebar) {
    return (
      <button
        onClick={() => setIsHidden(false)}
        className="fixed left-0 top-1/2 -translate-y-1/2 z-50 bg-primary text-primary-foreground p-2 rounded-r-lg shadow-lg hover:bg-primary/90 transition-colors"
        aria-label="Mostrar sidebar"
        title="Mostrar sidebar"
      >
        <PanelLeft className="w-5 h-5" />
      </button>
    )
  }
  
  return (
    <>
      {isMobile && !openMobile && (
        <button
          onClick={() => toggleSidebar()}
          className="fixed top-3 left-3 z-50 bg-primary text-primary-foreground p-2 rounded-lg shadow-lg hover:bg-primary/90 transition-colors"
          aria-label="Abrir menu"
        >
          <PanelLeft className="w-5 h-5" />
        </button>
      )}
      <Sidebar collapsible="icon">
      <SidebarHeader>
        <div className="relative">
          {!isCollapsed ? (
            <div className="px-4 py-2 space-y-3">
              {/* Logo + Título + Badge Ambiente */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                  <img 
                    src={logoDobraImg} 
                    alt="Dobra" 
                    className="h-10 w-auto object-contain opacity-90"
                  />
                  <div>
                    <h1 className="text-lg font-bold leading-tight">🏭 SmartProd</h1>
                    <p className="text-[10px] opacity-60">by Dobra Camisetas</p>
                  </div>
                </div>
              </div>
              
              {/* Status + Notificações */}
              <div className="flex items-center gap-2">
                <RealtimeStatusIndicator />
                <NotificationBell />
              </div>
            </div>
          ) : (
            <div className="px-2 py-2 flex flex-col items-center gap-2">
              <img 
                src={logoDobraImg} 
                alt="Dobra" 
                className="h-8 w-8 object-contain opacity-90 hover:opacity-100 transition-opacity"
                title="Dobra Camisetas Personalizadas"
              />
              <RealtimeStatusIndicator />
              <NotificationBell />
            </div>
          )}
          
          {/* Botão X para ocultar completamente (apenas desktop) */}
          {!isMobile && (
            <button
              onClick={() => setIsHidden(true)}
              className="absolute top-2 right-2 p-1 hover:bg-sidebar-accent rounded opacity-50 hover:opacity-100 transition-opacity"
              aria-label="Esconder sidebar"
              title="Esconder sidebar"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Menu</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {/* Menu items do Dashboard (somente para admin/gerente quando no dashboard) */}
              {(isAdminUser || isManager) && dashboardMenuItems
                .filter(item => !item.adminOnly || isAdminUser)
                .map((item) => {
                  const Icon = item.icon
                  if (item.path) {
                    const isActive = location.pathname === item.path
                    return (
                      <SidebarMenuItem key={item.path}>
                        <SidebarMenuButton asChild isActive={isActive} tooltip={item.label}>
                          <Link to={item.path}>
                            <Icon className="w-5 h-5" />
                            <span>{item.label}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )
                  }
                  const isActive = activeView === item.id
                  return (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        onClick={() => handleNavigation(item.id)}
                        isActive={isActive}
                        tooltip={item.label}
                      >
                        <Icon className="w-5 h-5" />
                        <span>{item.label}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )
                })}
              
              {/* Link para Produção - quando em páginas dedicadas */}
              {(isAdminUser || isManager) && !isOnDashboard && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Voltar ao Dashboard">
                    <Link to="/">
                      <LayoutDashboard className="w-5 h-5" />
                      <span>← Voltar ao Dashboard</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {/* Rotas dedicadas (baseado em permissões de setor) */}
              {dedicatedRoutes.map((route) => {
                // Dono do sistema (super_admin); com orgAdminAllowed, também o org_admin (Relatórios/auditoria)
                if (route.superAdminOnly && !isSuperAdminUser && !(route.orgAdminAllowed && isOrgAdminUser)) return null

                // Se é rota adminOnly e usuário não é admin, não mostrar
                if (route.adminOnly && !isAdminUser) return null
                
                // Se é rota apenas de manager e usuário não é manager/admin, não mostrar
                if (route.managerOnly && !isAdminUser && !isManager) return null
                
                // Se é rota de operador e usuário é manager/admin, não mostrar aqui
                if (route.operatorOnly && (isAdminUser || isManager)) return null
                
                // Se a rota tem setores definidos, verificar permissão
                if (route.sectors) {
                  const hasAccess = isAdminUser || isManager || route.sectors.some(s => 
                    userRole?.setores?.includes(s) || userRole?.setor === s
                  )
                  if (!hasAccess) return null
                }
                
                const Icon = route.icon
                const isActive = location.pathname === route.path || location.pathname.startsWith(route.path + '/')
                
                return (
                  <SidebarMenuItem key={route.path}>
                    <SidebarMenuButton asChild tooltip={route.label} isActive={isActive}>
                      <Link to={route.path}>
                        <Icon className="w-5 h-5" />
                        <span>{route.label}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* SEÇÃO: Operar Setores (Admin/Gerente) */}
        {(isAdminUser || isManager) && (
          <SidebarGroup>
            <SidebarGroupLabel>
              🔧 Operar Setores
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {loadingSectors ? (
                  <div className="flex items-center justify-center p-4">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                ) : (
                  productionSectors.map((sector) => {
                    const sectorViewId = `operator-sector-${sector.nome}`
                    const isActive = activeView === sectorViewId
                    
                    return (
                      <SidebarMenuItem key={sector.id}>
                        <SidebarMenuButton
                          onClick={() => handleNavigation(sectorViewId)}
                          isActive={isActive}
                          tooltip={sector.nome_exibicao}
                        >
                          <Wrench className="w-5 h-5" />
                          <div className="flex items-center gap-2 flex-1">
                            <span>{sector.nome_exibicao}</span>
                            <Badge 
                              className="text-xs px-1.5 py-0 ml-auto" 
                              style={{ 
                                backgroundColor: sector.cor_badge.startsWith('#') 
                                  ? sector.cor_badge 
                                  : undefined
                              }}
                            >
                              Ops
                            </Badge>
                          </div>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )
                  })
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter>
        {!isCollapsed ? (
          <div className="space-y-3 p-4">
            {/* Tema (densidade removida - agora cada módulo tem seu próprio toggle) */}
            <div className="flex items-center gap-2">
              <ThemeToggle variant="icon" />
            </div>
            
            {/* Link para Painel Admin (super_admin / org_admin — Story SAAS-3.4) */}
            <AdminPanelLink />

            {/* Troca de Setor Global (operadores multi-setor) */}
            <SectorSwitcher compact />
            
            {/* Usuário + Logout */}
            <div className="flex items-center justify-between gap-2">
              <Badge variant="secondary" className="text-xs">
                {getRoleIcon(userRole)} {getRoleLabel(userRole)}
              </Badge>
              {onSignOut && (
                <Button variant="outline" size="icon" onClick={onSignOut} className="h-7 w-7">
                  <LogOut className="h-3 w-3" />
                </Button>
              )}
            </div>
            

          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 py-2">
            {/* Densidade (collapsed) */}
            {effectiveCycleDensity && effectiveGetDensityIcon && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={effectiveCycleDensity}
                    className="h-8 w-8"
                  >
                    <span className="text-sm">{effectiveGetDensityIcon()}</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  Densidade
                </TooltipContent>
              </Tooltip>
            )}
            
            {/* Tema (collapsed) */}
            <ThemeToggle variant="icon" />
            
            {/* Usuário (collapsed) */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="p-1">
                  <span className="text-sm">{getRoleIcon(userRole)}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="right">
                {getRoleLabel(userRole)}
              </TooltipContent>
            </Tooltip>
            
            {/* Logout (collapsed) */}
            {onSignOut && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onSignOut}
                    className="h-8 w-8"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  Sair
                </TooltipContent>
              </Tooltip>
            )}
            
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
    </>
  )
}
